/**
 * Bid Management Hook
 * 
 * Manages the complete lifecycle of bids on RFQs (Requests for Quotation), from
 * submission by vendors to award by clients. Handles bid visibility, line item
 * pricing, and the award process.
 * 
 * **Bid Lifecycle:**
 * 1. Vendor discovers RFQ (public or invited)
 * 2. Vendor submits bid with line items for each specification
 * 3. Client reviews all bids and match scores
 * 4. Client awards bid to winning vendor
 * 5. Other bids automatically marked as "loser"
 * 6. Job status updated to "awarded"
 * 
 * **Visibility Rules:**
 * - Vendors see ONLY their own bids on an RFQ
 * - Clients see ALL bids submitted to their RFQs
 * - Admins can see all bids platform-wide
 * 
 * **Bid Components:**
 * - Lead time: Days required to complete work
 * - Line items: Price for each specification at each quantity option
 * - Notes: Vendor's proposal details, clarifications
 * - Attachments: Supporting documents, certifications
 * - Attestations: Compliance confirmations
 * - Valid until: Bid expiration date
 * 
 * **Award Process:**
 * - Creates award record linking job to winning bid
 * - Updates winning bid status to "winner"
 * - Updates other bids to "loser"
 * - Changes RFQ status to "awarded"
 * - Changes job status to "awarded"
 * - Triggers notifications to all bidders
 * 
 * **Key Features:**
 * - Role-based bid visibility
 * - Multi-line-item bid submission
 * - Bid withdrawal with reason tracking
 * - Automated award workflow
 * - Vendor profile enrichment
 * - Real-time notifications
 * 
 * **Use Cases:**
 * - Vendor bid submission interface
 * - Client bid review and comparison
 * - Bid award decision support
 * - Bid history and tracking
 * - Vendor performance monitoring
 * 
 * @param rfqId - The UUID of the RFQ to manage bids for
 * @param isClient - Whether the current user is a client (affects visibility)
 * @returns {Object} Bid management functions and state
 * @returns {Bid[]} bids - Array of bids (filtered by role)
 * @returns {boolean} loading - Loading state
 * @returns {string|null} error - Error message if any
 * @returns {Function} refetch - Manually refresh bids
 * @returns {Function} submitBid - Submit a new bid
 * @returns {Function} withdrawBid - Withdraw an existing bid
 * @returns {Function} awardBid - Award the job to a bid
 * 
 * @example
 * // Vendor submits bid
 * const { submitBid, loading } = useBids(rfqId, false);
 * 
 * const handleSubmit = async (bidData: BidSubmissionData) => {
 *   try {
 *     const bid = await submitBid(bidData, lineItems);
 *     navigate(`/rfqs/${rfqId}?tab=bids`);
 *     toast({ title: 'Bid submitted successfully' });
 *   } catch (error) {
 *     if (error.message.includes('profile must be complete')) {
 *       navigate('/profile/vendor');
 *     }
 *     toast({ title: 'Submission failed', variant: 'destructive' });
 *   }
 * };
 * 
 * @example
 * // Client reviews and awards bid
 * const { bids, awardBid } = useBids(rfqId, true);
 * 
 * const handleAward = async (bidId: string) => {
 *   await awardBid(bidId, {
 *     client_po_ref: 'PO-2024-001',
 *     award_notes: 'Best combination of price and experience'
 *   });
 *   toast({ title: 'Bid awarded successfully' });
 * };
 * 
 * @example
 * // Vendor withdraws bid
 * const { withdrawBid } = useBids(rfqId, false);
 * 
 * const handleWithdraw = async (bidId: string) => {
 *   await withdrawBid(
 *     bidId, 
 *     'Unable to meet required delivery date due to supply chain issues'
 *   );
 * };
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';
import { Bid, BidSubmissionData } from '@/lib/types/job';
import { BidLineItemInput } from './useBidLineItems';
import { TaxCalculation } from './useVendorBidTax';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

export const useBids = (rfqId: string, isClient: boolean = false) => {
  const [bids, setBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBids = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('bids')
        .select('*')
        .eq('rfq_id', rfqId);

      if (!isClient) {
        // Vendors can only see their own bids
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          query = query.eq('vendor_user_id', user.id);
        }
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      const bidsData = (data as any[]) || [];
      // Fetch vendor profiles in a separate query and merge client-side to avoid FK join requirement
      const vendorIds = Array.from(new Set(bidsData.map((b: any) => b.vendor_user_id).filter(Boolean)));
      if (vendorIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('vendor_profiles_enhanced')
          .select(`
            *,
            organization:organizations!vendor_profiles_enhanced_organization_id_fkey(id, name)
          `)
          .in('user_id', vendorIds);
        if (profilesError) {
          // If profiles fail to load, still return bids without profiles
          console.warn('useBids: Failed to load vendor profiles, proceeding without them', profilesError);
          setBids(bidsData as any[]);
        } else {
          console.log('useBids: Fetched vendor profiles:', profiles);
          const profileMap = new Map((profiles || []).map((p: any) => [p.user_id, p]));
          const enriched = bidsData.map((b: any) => ({ ...b, vendor_profile: profileMap.get(b.vendor_user_id) || null }));
          console.log('useBids: Enriched bids with profiles:', enriched);
          setBids(enriched as any[]);
        }
      } else {
        setBids(bidsData as any[]);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching bids:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch bids');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (rfqId) {
      fetchBids();
    }
  }, [rfqId, isClient]);

  const submitBid = async (
    bidData: Omit<BidSubmissionData, 'price_cents'>, 
    lineItems: BidLineItemInput[],
    taxCalculations?: TaxCalculation[]
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Calculate total vendor tax
      const totalVendorTaxCents = taxCalculations?.reduce((sum, calc) => sum + calc.vendorTaxCents, 0) || 0;
      const taxCalculationStatus = taxCalculations && taxCalculations.length > 0 ? 'calculated' : 'not_calculated';

      // Create bid with tax info
      const { data: bid, error: bidError } = await supabase
        .from('bids')
        .insert({
          rfq_id: rfqId,
          vendor_user_id: user.id,
          lead_time_days: bidData.lead_time_days,
          notes: bidData.notes,
          valid_until: bidData.valid_until,
          attestation_data: bidData.attestation_data,
          total_vendor_tax_cents: totalVendorTaxCents,
          tax_calculation_status: taxCalculationStatus,
        })
        .select()
        .single();

      if (bidError) {
        // Check if error is due to policy violation (compliance check failure)
        if (bidError.code === '42501' || bidError.message?.includes('policy')) {
          throw new Error('Your vendor profile must be complete and verified before submitting bids. Please complete your profile and request verification.');
        }
        throw bidError;
      }

      // Insert all line items
      const lineItemsWithBidId = lineItems.map(li => ({
        ...li,
        bid_id: bid.id
      }));

      const { data: insertedLineItems, error: lineItemsError } = await supabase
        .from('bid_line_items')
        .insert(lineItemsWithBidId)
        .select();

      if (lineItemsError) throw lineItemsError;

      // Insert tax calculations if provided
      if (taxCalculations && taxCalculations.length > 0 && insertedLineItems) {
        // Build a map of line items by spec+qty for lookup
        const lineItemMap = new Map(
          insertedLineItems.map(li => [`${li.specification_id}_${li.quantity_option}`, li.id])
        );

        const taxCalcsToInsert = taxCalculations.map(calc => {
          const lineItemId = lineItemMap.get(`${calc.specificationId}_${calc.quantityOption}`);
          return {
            bid_id: bid.id,
            bid_line_item_id: lineItemId,
            specification_id: calc.specificationId,
            quantity_option: calc.quantityOption,
            destination_location_id: calc.destinationLocationId,
            quantity: calc.quantity,
            taxable_amount_cents: calc.taxableAmountCents,
            calculated_tax_cents: calc.calculatedTaxCents,
            vendor_tax_cents: calc.vendorTaxCents,
            tax_rate_percentage: calc.taxRatePercentage,
            origin_zip: calc.originZip,
            destination_zip: calc.destinationZip,
            is_manually_edited: calc.isEdited,
            ziptax_response: calc.ziptaxResponse || null,
          };
        });

        const { error: taxError } = await supabase
          .from('bid_line_item_tax_calculations')
          .insert(taxCalcsToInsert);

        if (taxError) {
          console.error('Error saving tax calculations:', taxError);
          // Don't throw - tax calcs are supplementary
        }
      }

      // Notify via edge function
      await invokeEdgeFunction('bid-notify', {
        body: { rfqId },
        callerOperation: 'useBids.submitBid',
      });

      // Track bid submission
      const totalAmountCents = lineItems.reduce((sum, li) => sum + li.total_price_cents, 0);
      analytics.track({
        name: ANALYTICS_EVENTS.BID_SUBMITTED,
        category: 'transaction',
        properties: {
          rfq_id: rfqId,
          bid_id: bid.id,
          amount_cents: totalAmountCents,
          tax_cents: totalVendorTaxCents,
          turnaround_days: bidData.lead_time_days,
          line_item_count_bid: lineItems.length,
          has_tax_calculation: taxCalculations && taxCalculations.length > 0,
        },
      });

      await fetchBids();
      return bid;
    } catch (err) {
      console.error('Error submitting bid:', err);
      throw err;
    }
  };

  const withdrawBid = async (bidId: string, reason: string) => {
    try {
      const { error } = await supabase
        .from('bids')
        .update({ 
          status: 'withdrawn',
          notes: (bids.find(b => b.id === bidId)?.notes || '') + `\n\nWithdrawal reason: ${reason}`
        })
        .eq('id', bidId);

      if (error) throw error;

      await fetchBids();
    } catch (err) {
      console.error('Error withdrawing bid:', err);
      throw err;
    }
  };

  const resubmitBid = async (
    withdrawnBidId: string, 
    bidData: Omit<BidSubmissionData, 'price_cents'>, 
    lineItems: BidLineItemInput[],
    taxCalculations?: TaxCalculation[]
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Delete existing line items for the withdrawn bid
      const { error: deleteError } = await supabase
        .from('bid_line_items')
        .delete()
        .eq('bid_id', withdrawnBidId);

      if (deleteError) throw deleteError;

      // Delete existing tax calculations for the withdrawn bid
      const { error: deleteTaxError } = await supabase
        .from('bid_line_item_tax_calculations')
        .delete()
        .eq('bid_id', withdrawnBidId);

      if (deleteTaxError) {
        console.warn('Error deleting old tax calculations:', deleteTaxError);
        // Don't throw - continue with resubmission
      }

      // Calculate total vendor tax
      const totalVendorTaxCents = taxCalculations?.reduce((sum, calc) => sum + calc.vendorTaxCents, 0) || 0;
      const taxCalculationStatus = taxCalculations && taxCalculations.length > 0 ? 'calculated' : 'not_calculated';

      // Update the bid record - reset status and update all fields including tax
      const { data: bid, error: bidError } = await supabase
        .from('bids')
        .update({
          status: 'submitted',
          lead_time_days: bidData.lead_time_days,
          notes: bidData.notes,
          valid_until: bidData.valid_until,
          attestation_data: bidData.attestation_data,
          total_vendor_tax_cents: totalVendorTaxCents,
          tax_calculation_status: taxCalculationStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', withdrawnBidId)
        .select()
        .single();

      if (bidError) throw bidError;

      // Insert new line items
      const lineItemsWithBidId = lineItems.map(li => ({
        ...li,
        bid_id: withdrawnBidId
      }));

      const { data: insertedLineItems, error: lineItemsError } = await supabase
        .from('bid_line_items')
        .insert(lineItemsWithBidId)
        .select();

      if (lineItemsError) throw lineItemsError;

      // Insert tax calculations if provided
      if (taxCalculations && taxCalculations.length > 0 && insertedLineItems) {
        const lineItemMap = new Map(
          insertedLineItems.map(li => [`${li.specification_id}_${li.quantity_option}`, li.id])
        );

        const taxCalcsToInsert = taxCalculations.map(calc => {
          const lineItemId = lineItemMap.get(`${calc.specificationId}_${calc.quantityOption}`);
          return {
            bid_id: withdrawnBidId,
            bid_line_item_id: lineItemId,
            specification_id: calc.specificationId,
            quantity_option: calc.quantityOption,
            destination_location_id: calc.destinationLocationId,
            quantity: calc.quantity,
            taxable_amount_cents: calc.taxableAmountCents,
            calculated_tax_cents: calc.calculatedTaxCents,
            vendor_tax_cents: calc.vendorTaxCents,
            tax_rate_percentage: calc.taxRatePercentage,
            origin_zip: calc.originZip,
            destination_zip: calc.destinationZip,
            is_manually_edited: calc.isEdited,
            ziptax_response: calc.ziptaxResponse || null,
          };
        });

        const { error: taxError } = await supabase
          .from('bid_line_item_tax_calculations')
          .insert(taxCalcsToInsert);

        if (taxError) {
          console.error('Error saving tax calculations on resubmit:', taxError);
          // Don't throw - tax calcs are supplementary
        }
      }

      // Notify via edge function
      await invokeEdgeFunction('bid-notify', {
        body: { rfqId },
        callerOperation: 'useBids.resubmitBid',
      });

      // Track bid resubmission
      const totalAmountCents = lineItems.reduce((sum, li) => sum + li.total_price_cents, 0);
      analytics.track({
        name: ANALYTICS_EVENTS.BID_SUBMITTED,
        category: 'transaction',
        properties: {
          rfq_id: rfqId,
          bid_id: withdrawnBidId,
          amount_cents: totalAmountCents,
          tax_cents: totalVendorTaxCents,
          turnaround_days: bidData.lead_time_days,
          line_item_count_bid: lineItems.length,
          has_tax_calculation: taxCalculations && taxCalculations.length > 0,
          is_resubmission: true,
        },
      });

      await fetchBids();
      return bid;
    } catch (err) {
      console.error('Error resubmitting bid:', err);
      throw err;
    }
  };

  const awardBid = async (bidId: string, awardData: { client_po_ref?: string; award_notes?: string }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the bid and job info
      const { data: bid, error: bidError } = await supabase
        .from('bids')
        .select(`
          *,
          rfq:rfqs!inner(
            job_id,
            job:jobs!inner(*)
          )
        `)
        .eq('id', bidId)
        .single();

      if (bidError) throw bidError;
      if (!bid) throw new Error('Bid not found');

      // Create award
      const { error: awardError } = await supabase
        .from('awards')
        .insert({
          job_id: (bid as any).rfq.job_id,
          winning_bid_id: bidId,
          awarded_by_user_id: user.id,
          ...awardData
        });

      if (awardError) throw awardError;

      // Update bid status
      const { error: bidUpdateError } = await supabase
        .from('bids')
        .update({ status: 'winner' })
        .eq('id', bidId);

      if (bidUpdateError) throw bidUpdateError;

      // Update other bids to loser
      const { error: otherBidsError } = await supabase
        .from('bids')
        .update({ status: 'loser' })
        .eq('rfq_id', rfqId)
        .neq('id', bidId);

      if (otherBidsError) throw otherBidsError;

      // Update job and RFQ status
      const { error: jobUpdateError } = await supabase
        .from('jobs')
        .update({ status: 'awarded' })
        .eq('id', (bid as any).rfq.job_id);

      if (jobUpdateError) throw jobUpdateError;

      const { error: rfqUpdateError } = await supabase
        .from('rfqs')
        .update({ status: 'awarded' })
        .eq('id', rfqId);

      if (rfqUpdateError) throw rfqUpdateError;

      await fetchBids();
    } catch (err) {
      console.error('Error awarding bid:', err);
      throw err;
    }
  };

  return { 
    bids, 
    loading, 
    error, 
    refetch: fetchBids,
    submitBid,
    resubmitBid,
    withdrawBid,
    awardBid
  };
};

/**
 * Vendor Profile Management Hook
 * 
 * Provides access to the current vendor's profile data and methods for updating
 * profile information and checking compliance requirements for bid submission.
 * 
 * **Profile Requirements:**
 * - Business name
 * - Contact information
 * - Capabilities and certifications
 * - Insurance and tax documents (W-9)
 * - Verification status
 * 
 * **Compliance Checking:**
 * Before a vendor can submit bids, they must meet minimum compliance requirements:
 * - Profile must be verified by admin
 * - Business name must be set
 * - Required capabilities selected
 * 
 * This hook helps vendors understand what's missing and guides them through
 * profile completion before allowing bid submission.
 * 
 * @returns {Object} Vendor profile management functions
 * @returns {any|null} profile - Current vendor profile data
 * @returns {boolean} loading - Loading state
 * @returns {Function} updateProfile - Update profile data
 * @returns {Function} fetchProfile - Manually refresh profile
 * @returns {Function} checkCompliance - Check if profile meets bid requirements
 * 
 * @example
 * // Check compliance before showing bid form
 * const { profile, checkCompliance } = useVendorProfile();
 * 
 * const canSubmitBid = await checkCompliance();
 * 
 * if (!canSubmitBid) {
 *   return (
 *     <Alert>
 *       <AlertTitle>Profile Incomplete</AlertTitle>
 *       <AlertDescription>
 *         Please complete your vendor profile to submit bids.
 *         <Button onClick={() => navigate('/profile/vendor')}>
 *           Complete Profile
 *         </Button>
 *       </AlertDescription>
 *     </Alert>
 *   );
 * }
 * 
 * @example
 * // Update vendor profile
 * const { updateProfile } = useVendorProfile();
 * 
 * const handleSave = async (formData) => {
 *   await updateProfile({
 *     business_name: formData.businessName,
 *     capabilities: formData.selectedCapabilities
 *   });
 *   toast({ title: 'Profile updated' });
 * };
 */
export const useVendorProfile = () => {
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .select('*')
        .eq('user_id', userData.user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setProfile(data);
    } catch (error: any) {
      console.error('Failed to fetch vendor profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData: any) => {
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .upsert({
          user_id: userData.user.id,
          ...profileData,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      setProfile(data);
      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
      throw error;
    }
  };

  const checkCompliance = async () => {
    if (!profile) return false;
    return profile.is_verified && profile.business_name;
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return {
    profile,
    loading,
    updateProfile,
    fetchProfile,
    checkCompliance,
  };
};
